/**
 * Параметры подбора «Движа»: значение формы, справочники и преобразования
 * `UserMode` ↔ форма.
 *
 * Изменение v1.12: раньше это лежало в `app/screens/profile/ModeForm.tsx`
 * рядом с UI и тянулось оттуда двумя разными экранами. Параметры — модель
 * подбора, а не профиля: они живут в своей фиче, а экран остался один
 * (`app/screens/dvizh/DiscoverySetupScreen.tsx`).
 *
 * Изменение v1.3: единственный режим — friends. BS §17.4: обязательные поля
 * режима — источник `missing[]`; без них `GET /discovery/candidates`
 * отвечает `mode_requirements_missing`.
 */
import { RHYTHM_LABEL, RHYTHM_ORDER } from '@entities/candidate/vibe'
import type { City, ModeMissing, RhythmSlot, UserMode } from '@shared/api/types'

export interface ModePreferences {
  age: [number, number]
  /** Изменение v1.5: география — список городов, а не радиус в километрах. */
  cities: City[]
  topics: string[]
  formats: string[]
  rhythm: RhythmSlot[]
}

export const AGE_MIN = 18
export const AGE_MAX = 99
/** Контракт: от одного до пяти городов подбора. */
export const CITIES_MAX = 5

export const TOPICS = [
  'Спорт и активности',
  'Творчество',
  'Технологии',
  'Кино и сериалы',
  'Музыка',
  'Путешествия',
  'Настолки',
  'Еда и кафе',
  'Книги',
  'Учёба и карьера',
]

export const FORMATS = [
  'Погулять',
  'Кофе',
  'Спорт вместе',
  'Онлайн-общение',
  'Сходить на событие',
  'Совместные проекты',
]

/** Обязательные поля режима человеческим языком (BS §17.4, `ModeMissing`). */
export const MODE_MISSING_TEXT: Record<ModeMissing, string> = {
  age_min: 'возраст',
  age_max: 'возраст',
  cities: 'города',
  seeking_genders: 'кого ищете',
  goal: 'цель',
  topics: 'темы',
  formats: 'форматы общения',
}

export function describeMissing(missing: readonly string[]): string {
  const labels = [...new Set(missing.map((m) => MODE_MISSING_TEXT[m as ModeMissing] ?? 'параметры'))]
  return labels.join(', ')
}

export function fromUserMode(mode: UserMode): ModePreferences {
  return {
    age: [mode.age_min ?? AGE_MIN, mode.age_max ?? 45],
    cities: mode.cities ?? [],
    topics: mode.topics ?? [],
    formats: mode.formats ?? [],
    rhythm: mode.rhythm ?? [],
  }
}

/** Тело `PUT /me/modes/friends` из значения формы. */
export function toModeBody(value: ModePreferences, enabled: boolean): Record<string, unknown> {
  return {
    enabled,
    age_min: value.age[0],
    age_max: value.age[1],
    city_ids: value.cities.map((city) => city.id),
    topics: value.topics,
    formats: value.formats,
    rhythm: value.rhythm,
  }
}

export function isSamePreferences(a: ModePreferences, b: ModePreferences): boolean {
  return (
    a.age[0] === b.age[0] &&
    a.age[1] === b.age[1] &&
    sameList(
      a.cities.map((c) => c.id),
      b.cities.map((c) => c.id),
    ) &&
    sameList(a.topics, b.topics) &&
    sameList(a.formats, b.formats) &&
    sameList(a.rhythm, b.rhythm)
  )
}

function sameList(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && [...a].sort().join('|') === [...b].sort().join('|')
}

/** Что ещё не заполнено в самом значении формы (до сохранения). */
export function localMissing(value: ModePreferences): ModeMissing[] {
  const missing: ModeMissing[] = []
  if (!value.cities.length) missing.push('cities')
  if (!value.topics.length) missing.push('topics')
  if (!value.formats.length) missing.push('formats')
  return missing
}

/** «21 год», «22 года», «25 лет» — возраст в подписи ползунка. */
export function pluralYears(n: number): string {
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod10 === 1 && mod100 !== 11) return 'год'
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return 'года'
  return 'лет'
}

/**
 * Значение строки-перехода. Названия тем и форматов длинные, поэтому от трёх
 * выбранных показываем первое и «ещё N»: так строка не обрывается многоточием
 * ровно на том месте, где написано, сколько всего выбрано.
 */
export function summarize(items: readonly string[], empty = 'Не выбрано'): string {
  if (!items.length) return empty
  if (items.length <= 2) return items.join(', ')
  return `${items[0]} +${items.length - 1}`
}

/** Сводка ритма: у слотов свои подписи из `vibe.ts`, порядок — оттуда же. */
export function summarizeRhythm(rhythm: readonly RhythmSlot[], empty = 'Не выбрано'): string {
  const labels = RHYTHM_ORDER.filter((slot) => rhythm.includes(slot)).map((slot) => RHYTHM_LABEL[slot])
  return summarize(labels, empty)
}
